import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import QRCode from 'qrcode';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Report, convertTimestamp } from '../types/firebase';
import { isSameOrganizationReport } from '../utils/reportPrivacyDisplay';
import { buildMobileReportHtml, type CompanyProfile as MobileCompanyProfile } from './mobilePdfHtml';

interface ReportGenerationOptions {
  includePhotos?: boolean;
  includeMap?: boolean;
  reportType?: 'incident' | 'summary';
  /** When true, PDF layout matches mobile app (Site Info + Alert cards, User/Reader grid, Notes, Law Enforcement, footer) */
  mobileStyle?: boolean;
  /** Logged-in user's masterOwnerID; when set, PDF is blocked for other companies' reports. */
  viewerMasterOwnerId?: string;
}

/** Company profile for PDF header (from Firestore companies collection) */
interface CompanyProfile {
  companyName?: string;
  phone?: string;
  phoneNumber?: string;
  email?: string;
  infoEmail?: string;
  address?: string;
  address1?: string;
  streetAddress1?: string;
  address2?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  zip?: string;
  zipCode?: string;
  country?: string;
  logoUrl?: string;
  [key: string]: any;
}

// Extended Report interface to handle potential photo fields (inherits shareData from Report)
interface ExtendedReport extends Report {
  photos?: string[];
  photoUrls?: string[];
  images?: string[];
  scanImages?: string[];
  evidencePhotos?: string[];
  uris?: Array<{ id: string; img: string }>;
  photo?: string;
  sitesDetails?: {
    photo?: string;
    name?: string;
    address?: string;
    description?: string;
    contact?: string;
    devices?: number;
    latitude?: number;
    longitude?: number;
    [key: string]: any;
  };
  [key: string]: any;
}

export class EnhancedPDFGenerator {
  private doc: jsPDF;
  private pageWidth: number;
  private pageHeight: number;
  private margin: number;
  private currentY: number;
  
  // Colors (match mobile: #2c3e50 header, #b0262c alert, #1f7a3a ok)
  private primaryColor = { r: 44, g: 62, b: 80 };
  private accentRed = { r: 176, g: 38, b: 44 };
  private accentGreen = { r: 31, g: 122, b: 58 };
  private lightBg = { r: 248, g: 248, b: 248 };
  private grayBg = { r: 243, g: 244, b: 246 };
  private darkText = { r: 51, g: 51, b: 51 };
  private mediumGray = { r: 102, g: 102, b: 102 };

  constructor() {
    this.doc = new jsPDF();
    this.pageWidth = this.doc.internal.pageSize.width;
    this.pageHeight = this.doc.internal.pageSize.height;
    this.margin = 15;
    this.currentY = 0;
  }

  async generateIncidentReport(
    report: ExtendedReport,
    options: ReportGenerationOptions = {},
    company: CompanyProfile | null = null
  ): Promise<void> {
    try {
      if (options.mobileStyle !== false) {
        await this.generateMobileStyleReport(report, options, company);
        return;
      }
      // Legacy flow
      await this.addModernHeader(report);
      this.addTitleSection(report);
      await this.addSummaryAndMapSection(report);
      if (report.status === 'Skimmer Detected!') {
        this.addTechnicalDetailsSection(report);
      }
      if (options.includeMap !== false) await this.addLocationMap(report);
      if (options.includePhotos !== false) await this.addPhotos(report);
      if (this.currentY > this.pageHeight - 100) this.addNewPage();
      this.addNarrativeSection(report);
      this.addProfessionalFooter(report);
    } catch (error) {
      console.error('Error generating PDF report:', error);
      throw new Error('Failed to generate PDF report');
    }
  }

  /** Mobile-style PDF: header (company), report title, Site + Alert cards, User + Reader grid, Skimmer Images, Notes, Law Enforcement, footer */
  private async generateMobileStyleReport(
    report: ExtendedReport,
    options: ReportGenerationOptions,
    company: CompanyProfile | null
  ): Promise<void> {
    await this.addMobileStyleHeader(report, company);
    this.addMobileReportHeading();
    this.addMobileSiteAndAlertCards(report);
    this.addMobileUserAndReaderGrid(report);
    if (options.includePhotos !== false) await this.addPhotos(report);
    if (this.currentY > this.pageHeight - 80) this.addNewPage();
    this.addMobileNotesCard(report);
    const isNoSkimmer = (report.status || '') === 'No Skimmer Detected';
    if (!isNoSkimmer) this.addMobileLawEnforcementSection(report);
    this.addMobileStyleFooter(report);
  }

  private async addMobileStyleHeader(report: ExtendedReport, company: CompanyProfile | null): Promise<void> {
    const companyName = company?.companyName || 'Company';
    const firstLetter = (companyName || 'C').charAt(0).toUpperCase();
    // Header bar (gradient simulated with solid)
    this.doc.setFillColor(this.primaryColor.r, this.primaryColor.g, this.primaryColor.b);
    this.doc.rect(0, 0, this.pageWidth, 32, 'F');
    this.doc.setTextColor(255, 255, 255);
    // Logo circle (placeholder)
    this.doc.setDrawColor(255, 255, 255);
    this.doc.setLineWidth(0.5);
    this.doc.circle(28, 16, 10, 'S');
    this.doc.setFontSize(16);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(firstLetter, 28, 18, { align: 'center' });
    this.doc.setFontSize(18);
    this.doc.text(companyName, 44, 18);
    // Contact right
    const contactLines: string[] = [];
    const addr = company?.address1 || company?.streetAddress1 || company?.address || '';
    if (addr) contactLines.push(addr);
    const city = [company?.city, company?.state].filter(Boolean).join(', ');
    const zip = company?.zip || company?.zipCode || '';
    const country = company?.country || '';
    if ([city, zip, country].filter(Boolean).length) contactLines.push([city, zip, country].filter(Boolean).join(' '));
    if (company?.phone || company?.phoneNumber) contactLines.push(String(company?.phone || company?.phoneNumber));
    if (company?.email || company?.infoEmail) contactLines.push(String(company?.email || company?.infoEmail));
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'normal');
    let cy = 12;
    contactLines.slice(0, 4).forEach((line) => {
      this.doc.text(line, this.pageWidth - this.margin, cy, { align: 'right' });
      cy += 5;
    });
    this.currentY = 40;
  }

  private addMobileReportHeading(): void {
    this.doc.setFillColor(this.grayBg.r, this.grayBg.g, this.grayBg.b);
    this.doc.rect(0, this.currentY, this.pageWidth, 22, 'F');
    this.doc.setFontSize(22);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(this.primaryColor.r, this.primaryColor.g, this.primaryColor.b);
    this.doc.text('Skimmer Detection Report', this.pageWidth / 2, this.currentY + 14, { align: 'center' });
    this.doc.setDrawColor(229, 231, 235);
    this.doc.setLineWidth(0.5);
    this.doc.line(0, this.currentY + 22, this.pageWidth, this.currentY + 22);
    this.currentY += 30;
  }

  private addMobileSiteAndAlertCards(report: ExtendedReport): void {
    const cardWidth = (this.pageWidth - this.margin * 2 - 12) / 2;
    const cardHeight = 52;
    const left = this.margin;
    const right = this.margin + cardWidth + 12;
    const status = (report.status || '').replace('!', '').trim();
    const isNoSkimmer = status === 'No Skimmer Detected';
    const reportDate = convertTimestamp(report.createdAt);
    const scanTime = reportDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    const reportType = (report as any).isManualEntry ? 'Manual Entry' : 'Device Scan';
    const siteName = report.readerDetails?.siteName || '—';
    const siteAddress = report.location?.address || '—';

    // Site Info card (gray gradient-like)
    this.doc.setFillColor(207, 214, 222);
    this.doc.setDrawColor(0, 0, 0);
    this.doc.setLineWidth(0.02);
    this.doc.roundedRect(left, this.currentY, cardWidth, cardHeight, 3, 3, 'FD');
    this.doc.setFontSize(11);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(this.primaryColor.r, this.primaryColor.g, this.primaryColor.b);
    this.doc.text('Site Info', left + cardWidth / 2, this.currentY + 10, { align: 'center' });
    this.doc.setDrawColor(44, 62, 80);
    this.doc.setLineWidth(0.2);
    this.doc.line(left, this.currentY + 14, left + cardWidth, this.currentY + 14);
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(this.darkText.r, this.darkText.g, this.darkText.b);
    const siteLines = this.doc.splitTextToSize(`${siteName}\n${siteAddress}`, cardWidth - 12);
    this.doc.text(siteLines, left + 6, this.currentY + 22);

    // Alert card (red or green)
    this.doc.setFillColor(isNoSkimmer ? 221 : 244, isNoSkimmer ? 244 : 223, isNoSkimmer ? 226 : 226);
    this.doc.setDrawColor(0, 0, 0);
    this.doc.setLineWidth(0.02);
    this.doc.roundedRect(right, this.currentY, cardWidth, cardHeight, 3, 3, 'FD');
    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(isNoSkimmer ? this.accentGreen.r : this.accentRed.r, isNoSkimmer ? this.accentGreen.g : this.accentRed.g, isNoSkimmer ? this.accentGreen.b : this.accentRed.b);
    this.doc.text(isNoSkimmer ? 'No Skimmer Detected' : 'Skimmer Detected', right + cardWidth / 2, this.currentY + 10, { align: 'center' });
    this.doc.line(right, this.currentY + 14, right + cardWidth, this.currentY + 14);
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(this.darkText.r, this.darkText.g, this.darkText.b);
    this.doc.text(`Scan Time: ${scanTime}`, right + 6, this.currentY + 22);
    this.doc.text(`Report Type: ${reportType}`, right + 6, this.currentY + 30);
    this.currentY += cardHeight + 14;
  }

  private addMobileUserAndReaderGrid(report: ExtendedReport): void {
    const cardWidth = (this.pageWidth - this.margin * 2 - 12) / 2;
    const cardHeight = 44;
    const left = this.margin;
    const right = this.margin + cardWidth + 12;

    const userName = report.user?.name || '—';
    const userRole = report.user?.role || '';
    const userEmail = report.user?.email || '';
    const deviceLocation = (report as any).readerDetails?.device?.specificLocation || (report as any).readerDetails?.device?.location || '';
    const deviceType = (report as any).readerDetails?.device?.deviceType || '';
    const modelName = (report as any).readerDetails?.device?.modelName || '';

    this.doc.setFillColor(207, 214, 222);
    this.doc.roundedRect(left, this.currentY, cardWidth, cardHeight, 3, 3, 'FD');
    this.doc.setFontSize(11);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(this.primaryColor.r, this.primaryColor.g, this.primaryColor.b);
    this.doc.text('User Info', left + cardWidth / 2, this.currentY + 10, { align: 'center' });
    this.doc.line(left, this.currentY + 14, left + cardWidth, this.currentY + 14);
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(this.darkText.r, this.darkText.g, this.darkText.b);
    let ty = this.currentY + 20;
    this.doc.text(userName, left + 6, ty);
    if (userRole) { ty += 6; this.doc.text(`Position: ${userRole}`, left + 6, ty); }
    if (userEmail) { ty += 6; this.doc.text(`Email: ${userEmail}`, left + 6, ty); }

    this.doc.setFillColor(207, 214, 222);
    this.doc.roundedRect(right, this.currentY, cardWidth, cardHeight, 3, 3, 'FD');
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Reader Info', right + cardWidth / 2, this.currentY + 10, { align: 'center' });
    this.doc.line(right, this.currentY + 14, right + cardWidth, this.currentY + 14);
    this.doc.setFont('helvetica', 'normal');
    ty = this.currentY + 20;
    if (deviceLocation) { this.doc.text(`Reader Location: ${deviceLocation}`, right + 6, ty); ty += 6; }
    if (deviceType) { this.doc.text(`Reader Type: ${deviceType}`, right + 6, ty); ty += 6; }
    if (modelName) this.doc.text(`Reader Model: ${modelName}`, right + 6, ty);
    this.currentY += cardHeight + 14;
  }

  private addMobileNotesCard(report: ExtendedReport): void {
    const notesValue = (report as any).shareData?.feedback?.value || report.message || '';
    const notes = (typeof notesValue === 'string' ? notesValue : '').trim() || 'N/A';
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'normal');
    const lines = this.doc.splitTextToSize(notes, this.pageWidth - this.margin * 2 - 16);
    const lineHeight = 5;
    const cardHeight = Math.max(36, 18 + lines.length * lineHeight);
    this.doc.setFillColor(255, 243, 205);
    this.doc.setDrawColor(255, 234, 167);
    this.doc.setLineWidth(0.3);
    this.doc.roundedRect(this.margin, this.currentY, this.pageWidth - this.margin * 2, cardHeight, 3, 3, 'FD');
    this.doc.setFontSize(11);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(133, 100, 4);
    this.doc.text('Notes', this.margin + 8, this.currentY + 10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(9);
    this.doc.text(lines, this.margin + 8, this.currentY + 20);
    this.currentY += cardHeight + 8;
  }

  private addMobileLawEnforcementSection(report: ExtendedReport): void {
    const law = (report as any).lawEnforcement;
    const reported = !!(law?.reportedToLawEnforcement ?? law?.reported);
    const department = law?.department ?? '';
    const contactName = law?.contactName ?? '';
    const contactPhone = law?.contactPhone ?? '';
    const caseNumber = law?.caseNumber ?? (report as any).caseNumber ?? '';
    const reportedAt = law?.reportedAt;
    let reportedDate = 'N/A';
    if (reportedAt) {
      try {
        const d = reportedAt?.toDate ? reportedAt.toDate() : (reportedAt && typeof reportedAt === 'object' && 'seconds' in reportedAt ? new Date((reportedAt as any).seconds * 1000) : new Date(reportedAt));
        reportedDate = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
      } catch {
        reportedDate = String(reportedAt);
      }
    }
    this.currentY += 6;
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(133, 100, 4);
    this.doc.text(reported ? 'Reported to Law Enforcement' : 'Not Reported to Law Enforcement', this.margin, this.currentY);
    this.currentY += 6;
    if (reported) {
      this.doc.setTextColor(this.darkText.r, this.darkText.g, this.darkText.b);
      if (department) { this.doc.text(`Department: ${department}`, this.margin, this.currentY); this.currentY += 5; }
      if (contactName) { this.doc.text(`Contact: ${contactName}`, this.margin, this.currentY); this.currentY += 5; }
      if (contactPhone) { this.doc.text(`Phone: ${contactPhone}`, this.margin, this.currentY); this.currentY += 5; }
      if (caseNumber) { this.doc.text(`Case Number: ${caseNumber}`, this.margin, this.currentY); this.currentY += 5; }
      this.doc.text(`Reported Date: ${reportedDate}`, this.margin, this.currentY);
      this.currentY += 5;
    }
    this.currentY += 8;
  }

  private addMobileStyleFooter(report: ExtendedReport): void {
    const footerY = this.pageHeight - 14;
    this.doc.setFillColor(this.primaryColor.r, this.primaryColor.g, this.primaryColor.b);
    this.doc.rect(0, footerY - 12, this.pageWidth, 26, 'F');
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(11);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text('Detected by SkimBuster  |  Report ID: ' + (report.id || '—'), this.pageWidth / 2, footerY + 2, { align: 'center' });
    this.doc.setFontSize(9);
    this.doc.text('micclo.com', this.pageWidth - this.margin, footerY + 2, { align: 'right' });
  }

  private async addModernHeader(report: Report): Promise<void> {
    // Header background
    this.doc.setFillColor(this.primaryColor.r, this.primaryColor.g, this.primaryColor.b);
    this.doc.rect(0, 0, this.pageWidth, 35, 'F');
    
    // Shield logo placeholder
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text('S', 22, 18);
    
    // Draw shield outline
    this.doc.setDrawColor(255, 255, 255);
    this.doc.setLineWidth(0.5);
    this.doc.circle(22, 15, 8);
    
    // Company name
    this.doc.setFontSize(20);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('SKIMBUSTER', 35, 20);
    
    // Date and time in header
    const reportDate = convertTimestamp(report.createdAt);
    this.doc.setFontSize(11);
    this.doc.setFont('helvetica', 'normal');
    const dateStr = reportDate.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: 'numeric' 
    });
    const timeStr = reportDate.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    
    this.doc.text(dateStr, this.pageWidth - 50, 15);
    this.doc.text(timeStr, this.pageWidth - 50, 22);
    
    this.currentY = 45;
  }

  private addTitleSection(report: Report): void {
    this.doc.setTextColor(this.darkText.r, this.darkText.g, this.darkText.b);
    this.doc.setFontSize(22);
    this.doc.setFont('helvetica', 'bold');
    
    const title = report.status === 'Skimmer Detected!' 
      ? 'Skimmer Detection Incident Report'
      : 'Security Scan Report';
    
    this.doc.text(title, this.margin, this.currentY);
    this.currentY += 15;
  }

  private async addSummaryAndMapSection(report: Report): Promise<void> {
    const leftColumnWidth = 110;
    const rightColumnWidth = 75;
    const columnGap = 10;
    
    // Left column - Incident Summary
    const summaryBoxHeight = 85;
    this.doc.setFillColor(this.lightBg.r, this.lightBg.g, this.lightBg.b);
    this.doc.rect(this.margin, this.currentY, leftColumnWidth, summaryBoxHeight, 'F');
    
    // Summary title
    this.doc.setFontSize(14);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(this.darkText.r, this.darkText.g, this.darkText.b);
    this.doc.text('Incident Summary', this.margin + 5, this.currentY + 12);
    
    // Summary content
    const reportDate = convertTimestamp(report.createdAt);
    const summaryY = this.currentY + 25;
    
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    
    // Date/Time
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('• Date/Time Detected:', this.margin + 5, summaryY);
    this.doc.setFont('helvetica', 'normal');
    const dateTimeStr = `${reportDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    })} - ${reportDate.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    })}`;
    this.doc.text(dateTimeStr, this.margin + 5, summaryY + 6);
    
    // Location
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('• Location:', this.margin + 5, summaryY + 15);
    this.doc.setFont('helvetica', 'normal');
    const siteName = report.readerDetails?.siteName || 'Unknown';
    const address = report.location?.address || 'Unknown';
    const locationStr = siteName !== address ? `${siteName} - ${address}` : siteName;
    const locationLines = this.doc.splitTextToSize(locationStr, leftColumnWidth - 10);
    this.doc.text(locationLines, this.margin + 5, summaryY + 21);
    
    // Device ID
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('• Device ID:', this.margin + 5, summaryY + 33);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(`SB-${report.id?.substring(0, 10).toUpperCase() || 'N/A'}`, this.margin + 5, summaryY + 39);
    
    // Reported By
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('• Reported By:', this.margin + 5, summaryY + 48);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(`${report.user?.name || 'Unknown'} (${report.user?.role || 'Unknown'})`, this.margin + 5, summaryY + 54);
    
    // Status with icon
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('• Status:', this.margin + 5, summaryY + 63);
    if (report.status === 'Skimmer Detected!') {
      // Red triangle warning icon
      this.doc.setFillColor(this.accentRed.r, this.accentRed.g, this.accentRed.b);
      const triangleX = this.margin + 28;
      const triangleY = summaryY + 60;
      this.doc.triangle(triangleX, triangleY, triangleX - 3, triangleY + 5, triangleX + 3, triangleY + 5, 'F');
      
      this.doc.setTextColor(this.accentRed.r, this.accentRed.g, this.accentRed.b);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Confirmed Skimmer Found', this.margin + 35, summaryY + 63);
    } else if (report.status === 'No Skimmer Detected') {
      this.doc.setTextColor(this.accentGreen.r, this.accentGreen.g, this.accentGreen.b);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('✓ No Threat Detected', this.margin + 28, summaryY + 63);
    } else {
      this.doc.setTextColor(255, 140, 0);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(report.status, this.margin + 28, summaryY + 63);
    }
    
    // Right column - Map placeholder and QR code
    const rightColumnX = this.margin + leftColumnWidth + columnGap;
    
    // Map placeholder (small version)
    const mapHeight = 40;
    this.doc.setFillColor(this.grayBg.r, this.grayBg.g, this.grayBg.b);
    this.doc.setDrawColor(200, 200, 200);
    this.doc.rect(rightColumnX, this.currentY, rightColumnWidth, mapHeight, 'FD');
    
    // Add a simple map representation
    const coords = this.getReportCoordinates(report);
    
    if (coords.lat && coords.lng) {
      // Draw a location pin in center
      const mapCenterX = rightColumnX + rightColumnWidth / 2;
      const mapCenterY = this.currentY + mapHeight / 2;
      
      this.doc.setFillColor(220, 20, 60);
      this.doc.circle(mapCenterX, mapCenterY - 3, 2, 'F');
      this.doc.triangle(
        mapCenterX, mapCenterY - 1,
        mapCenterX - 2, mapCenterY + 2,
        mapCenterX + 2, mapCenterY + 2,
        'F'
      );
      
      // Display coordinates
      this.doc.setFontSize(7);
      this.doc.setTextColor(this.mediumGray.r, this.mediumGray.g, this.mediumGray.b);
      this.doc.text(`${coords.lat.toFixed(4)}°, ${coords.lng.toFixed(4)}°`, rightColumnX + rightColumnWidth/2, this.currentY + mapHeight - 5, { align: 'center' });
    } else {
      // No coordinates available
      this.doc.setFontSize(8);
      this.doc.setTextColor(this.mediumGray.r, this.mediumGray.g, this.mediumGray.b);
      this.doc.text('Location', rightColumnX + rightColumnWidth/2, this.currentY + mapHeight/2 - 3, { align: 'center' });
      this.doc.text('Unavailable', rightColumnX + rightColumnWidth/2, this.currentY + mapHeight/2 + 3, { align: 'center' });
    }
    
    // QR Code section
    const qrY = this.currentY + mapHeight + 10;
    try {
      const verificationUrl = `https://skimbuster.app/verify/${report.id}`;
      const qrCodeDataUrl = await QRCode.toDataURL(verificationUrl, {
        width: 150,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      
      const qrSize = 35;
      const qrX = rightColumnX + (rightColumnWidth - qrSize) / 2;
      this.doc.addImage(qrCodeDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
      
      // QR code label
      this.doc.setFontSize(8);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(this.mediumGray.r, this.mediumGray.g, this.mediumGray.b);
      this.doc.text('QR CODE', rightColumnX + rightColumnWidth/2, qrY - 3, { align: 'center' });
      this.doc.text('Scan to verify', rightColumnX + rightColumnWidth/2, qrY + qrSize + 5, { align: 'center' });
    } catch (error) {
      console.warn('Failed to generate QR code:', error);
    }
    
    this.currentY += summaryBoxHeight + 20;
  }

  private addTechnicalDetailsSection(report: Report): void {
    const leftColumnWidth = 80;
    const rightColumnWidth = this.pageWidth - (this.margin * 2) - leftColumnWidth - 5;
    const techBoxHeight = 45;
    
    // Technical details title bar
    this.doc.setFillColor(this.grayBg.r, this.grayBg.g, this.grayBg.b);
    this.doc.rect(this.margin, this.currentY, this.pageWidth - (this.margin * 2), 8, 'F');
    
    this.doc.setFontSize(11);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(this.darkText.r, this.darkText.g, this.darkText.b);
    this.doc.text('TECHNICAL DETAILS', this.margin + 5, this.currentY + 6);
    
    this.currentY += 8;
    
    // Left column - Technical labels
    this.doc.setFillColor(255, 255, 255);
    this.doc.rect(this.margin, this.currentY, leftColumnWidth, techBoxHeight, 'F');
    
    this.doc.setTextColor(this.mediumGray.r, this.mediumGray.g, this.mediumGray.b);
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'bold');
    
    const techLabels = [
      'DETECTION SCORE',
      'DEVICE MODEL',
      'SCAN DURATION',
      'RAW SHIFT PEAK',
      'EXTREME SPIKES'
    ];
    
    let labelY = this.currentY + 10;
    techLabels.forEach(label => {
      this.doc.text(label, this.margin + 5, labelY);
      labelY += 8;
    });
    
    // Right column - Technical values
    this.doc.setFillColor(this.lightBg.r, this.lightBg.g, this.lightBg.b);
    this.doc.rect(this.margin + leftColumnWidth, this.currentY, rightColumnWidth, techBoxHeight, 'F');
    
    // Get or generate technical data
    const scanData = this.extractScanData(report);
    
    this.doc.setTextColor(this.darkText.r, this.darkText.g, this.darkText.b);
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    
    let valueY = this.currentY + 10;
    const techValues = [
      scanData.detectionScore,
      report.readerDetails?.device?.modelName || 'Unknown Model',
      scanData.scanDuration,
      scanData.rawShiftPeak,
      scanData.extremeSpikes
    ];
    
    techValues.forEach(value => {
      this.doc.text(String(value), this.margin + leftColumnWidth + 5, valueY);
      valueY += 8;
    });
    
    // Magnetic Signature label
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(this.mediumGray.r, this.mediumGray.g, this.mediumGray.b);
    this.doc.text('Magnetic Signature Analysis', this.margin + leftColumnWidth + 5, this.currentY + 2);
    
    this.currentY += techBoxHeight + 15;
  }

  private async addLocationMap(report: ExtendedReport): Promise<void> {
    try {
      const coords = this.getReportCoordinates(report);
      const address = this.sanitizeTextValue(report.location?.address, '');
      
      // Only show this section if we have coordinates OR an address
      if ((!coords.lat || !coords.lng) && !address) {
        return; // Skip if no location data available
      }

      // Check if we need a new page
      if (this.currentY > this.pageHeight - 120) {
        this.addNewPage();
      }

      this.doc.setFontSize(12);
      this.doc.setFont('helvetica', 'bold');
      this.doc.setTextColor(44, 62, 80);
      this.doc.text('Location Details', this.margin, this.currentY);
      this.currentY += 10;

      // Create a map placeholder with more detail
      const mapWidth = this.pageWidth - (this.margin * 2);
      const mapHeight = 80;
      
      // Map background
      this.doc.setFillColor(245, 245, 245);
      this.doc.setDrawColor(200, 200, 200);
      this.doc.rect(this.margin, this.currentY, mapWidth, mapHeight, 'FD');
      
      // Add grid lines for map effect
      this.doc.setDrawColor(230, 230, 230);
      this.doc.setLineWidth(0.1);
      
      // Vertical lines
      for (let x = this.margin + 20; x < this.margin + mapWidth; x += 20) {
        this.doc.line(x, this.currentY, x, this.currentY + mapHeight);
      }
      
      // Horizontal lines
      for (let y = this.currentY + 20; y < this.currentY + mapHeight; y += 20) {
        this.doc.line(this.margin, y, this.margin + mapWidth, y);
      }
      
      // Center marker
      const mapCenterX = this.margin + (mapWidth / 2);
      const mapCenterY = this.currentY + (mapHeight / 2);
      
      // Draw location pin
      this.doc.setFillColor(220, 20, 60);
      this.doc.circle(mapCenterX, mapCenterY - 5, 3, 'F');
      this.doc.triangle(
        mapCenterX, mapCenterY - 2,
        mapCenterX - 3, mapCenterY + 3,
        mapCenterX + 3, mapCenterY + 3,
        'F'
      );
      
      // Add coordinate and address information
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.setTextColor(44, 62, 80);
      
      // Show site name if available
      const siteName = this.sanitizeTextValue(report.readerDetails?.siteName, 'Incident Location');
      this.doc.text(siteName, mapCenterX, mapCenterY - 15, { align: 'center' });
      
      // Show coordinates if available
      if (coords.lat && coords.lng) {
        this.doc.setFontSize(9);
        this.doc.setFont('helvetica', 'normal');
        this.doc.setTextColor(100, 100, 100);
        this.doc.text(`GPS: ${coords.lat.toFixed(6)}°, ${coords.lng.toFixed(6)}°`, mapCenterX, mapCenterY + 15, { align: 'center' });
      }
      
      // Add address if available
      if (address) {
        this.doc.setFontSize(8);
        this.doc.setTextColor(80, 80, 80);
        const addressLines = this.doc.splitTextToSize(address, mapWidth - 20);
        const startY = coords.lat && coords.lng ? mapCenterY + 25 : mapCenterY + 15;
        if (addressLines.length > 0) {
          this.doc.text(addressLines[0], mapCenterX, startY, { align: 'center' });
          if (addressLines.length > 1) {
            this.doc.text(addressLines[1], mapCenterX, startY + 5, { align: 'center' });
          }
        }
      }
      
      this.currentY += mapHeight + 15;
    } catch (error) {
      console.warn('Failed to add location map:', error);
    }
  }

  private async addPhotos(report: ExtendedReport): Promise<void> {
    try {
      // Extract site photo and evidence photos separately
      let sitePhoto: string | null = null;
      let evidencePhotos: string[] = [];
      
      // Check for site photo from sitesDetails
      if (report.sitesDetails?.photo) {
        sitePhoto = report.sitesDetails.photo;
      }
      
      // Check for evidence photos from shareData.images.uris first (preferred)
      if (report.shareData?.images?.uris && Array.isArray(report.shareData.images.uris)) {
        evidencePhotos = report.shareData.images.uris.map(uri => uri.img).filter(img => img);
      }
      // Fallback to other photo fields
      else if (report.uris && Array.isArray(report.uris)) {
        evidencePhotos = report.uris.map(uri => uri.img).filter(img => img);
      }
      else if (report.photo) {
        evidencePhotos = [report.photo];
      }
      else {
        evidencePhotos = report.photos || report.photoUrls || report.images || 
                        report.scanImages || report.evidencePhotos || [];
      }
      
      // Combine all photos for display (site photo first if available)
      const allPhotos: { url: string; label: string }[] = [];
      
      if (sitePhoto) {
        allPhotos.push({ url: sitePhoto, label: 'Site Photo' });
      }
      
      evidencePhotos.forEach((photo, index) => {
        allPhotos.push({ url: photo, label: `Skimmer Image ${index + 1}` });
      });
      
      if (allPhotos.length === 0) {
        return; // No photos to add
      }

      // Check if we need a new page
      if (this.currentY > this.pageHeight - 100) {
        this.addNewPage();
      }

      this.doc.setFontSize(12);
      this.doc.setFont('helvetica', 'bold');
      this.doc.setTextColor(44, 62, 80);
      this.doc.text('Skimmer Images', this.margin, this.currentY);
      this.currentY += 10;

      // Calculate photo dimensions
      const maxPhotosPerRow = 2;
      const photoMargin = 5;
      const availableWidth = this.pageWidth - (this.margin * 2);
      const photoWidth = (availableWidth - (photoMargin * (maxPhotosPerRow - 1))) / maxPhotosPerRow;
      const photoHeight = photoWidth * 0.75; // 4:3 aspect ratio

      let photoX = this.margin;
      let photoY = this.currentY;
      let photosInCurrentRow = 0;

      // Add photos (limit to first 4 to save space)
      const photosToAdd = allPhotos.slice(0, 4);
      
      for (let i = 0; i < photosToAdd.length; i++) {
        const photoItem = photosToAdd[i];
        
        // Check if we need a new page
        if (photoY + photoHeight > this.pageHeight - 40) {
          this.addNewPage();
          photoY = this.currentY;
          photoX = this.margin;
          photosInCurrentRow = 0;
        }
        
        try {
          // Try to add the image
          // Note: This requires the image to be accessible via URL or base64
          if (photoItem.url.startsWith('data:image') || photoItem.url.startsWith('http')) {
            // Draw border around photo
            this.doc.setDrawColor(200, 200, 200);
            this.doc.setLineWidth(0.5);
            this.doc.rect(photoX, photoY, photoWidth, photoHeight, 'D');
            
            // Add the image
            this.doc.addImage(photoItem.url, 'JPEG', photoX + 1, photoY + 1, photoWidth - 2, photoHeight - 2);
            
            // Add photo number label
            this.doc.setFillColor(44, 62, 80);
            this.doc.rect(photoX, photoY + photoHeight, photoWidth, 10, 'F');
            
            this.doc.setFontSize(8);
            this.doc.setFont('helvetica', 'bold');
            this.doc.setTextColor(255, 255, 255);
            this.doc.text(photoItem.label, photoX + photoWidth/2, photoY + photoHeight + 7, { align: 'center' });
          } else {
            // Draw placeholder for inaccessible images
            this.doc.setFillColor(240, 240, 240);
            this.doc.setDrawColor(200, 200, 200);
            this.doc.rect(photoX, photoY, photoWidth, photoHeight, 'FD');
            
            this.doc.setFontSize(10);
            this.doc.setTextColor(150, 150, 150);
            this.doc.text(`Photo ${i + 1}`, photoX + photoWidth/2, photoY + photoHeight/2 - 5, { align: 'center' });
            this.doc.text('(Not available)', photoX + photoWidth/2, photoY + photoHeight/2 + 5, { align: 'center' });
          }
        } catch (imgError) {
          console.warn(`Failed to add photo ${i + 1}:`, imgError);
          // Draw placeholder on error
          this.doc.setFillColor(240, 240, 240);
          this.doc.setDrawColor(200, 200, 200);
          this.doc.rect(photoX, photoY, photoWidth, photoHeight, 'FD');
          
          this.doc.setFontSize(10);
          this.doc.setTextColor(150, 150, 150);
          this.doc.text(`Photo ${i + 1}`, photoX + photoWidth/2, photoY + photoHeight/2 - 5, { align: 'center' });
          this.doc.text('(Loading error)', photoX + photoWidth/2, photoY + photoHeight/2 + 5, { align: 'center' });
        }
        
        // Move to next photo position
        photosInCurrentRow++;
        if (photosInCurrentRow >= maxPhotosPerRow) {
          photoX = this.margin;
          photoY += photoHeight + 15;
          photosInCurrentRow = 0;
        } else {
          photoX += photoWidth + photoMargin;
        }
      }
      
      // Update currentY based on photos added
      if (photosInCurrentRow > 0) {
        this.currentY = photoY + photoHeight + 20;
      } else {
        this.currentY = photoY + 10;
      }
      
      // Add note about total photos if more than displayed
      if (allPhotos.length > 4) {
        this.doc.setFontSize(8);
        this.doc.setFont('helvetica', 'italic');
        this.doc.setTextColor(100, 100, 100);
        this.doc.text(`Note: ${allPhotos.length - 4} additional photo(s) available in full report`, this.margin, this.currentY);
        this.currentY += 10;
      }
    } catch (error) {
      console.warn('Failed to add photos section:', error);
    }
  }

  private addNarrativeSection(report: Report): void {
    // Narrative section
    this.doc.setFontSize(14);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(this.darkText.r, this.darkText.g, this.darkText.b);
    this.doc.text('Narrative', this.margin, this.currentY);
    this.currentY += 10;
    
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    
    let narrative = '';
    const deviceType = report.readerDetails?.device?.deviceType || 'the device';
    const siteName = report.readerDetails?.siteName || 'the location';
    const address = report.location?.address || 'the specified address';
    
    if (report.status === 'Skimmer Detected!') {
      narrative = `Upon scanning ${deviceType} at ${siteName}, ${address}, the Skimbuster device detected abnormal magnetic field readings consistent with a deep insert skimmer. Visual inspection confirmed the presence of unauthorized hardware. The device was immediately quarantined and local authorities were notified. Immediate replacement and forensic analysis are strongly recommended.`;
    } else if (report.status === 'No Skimmer Detected') {
      narrative = `Comprehensive security scan performed on ${deviceType} at ${siteName}. All electromagnetic signatures within normal parameters. No evidence of tampering or unauthorized devices detected. The card reader is operating normally and is safe for customer use.`;
    } else {
      narrative = `Security scan initiated for ${deviceType} at ${siteName}. ${report.message || 'Scan results pending further analysis.'}`;
    }
    
    if (report.message && report.message.trim() !== '') {
      narrative += `\n\nAdditional Notes: ${report.message}`;
    }
    
    const lines = this.doc.splitTextToSize(narrative, this.pageWidth - (this.margin * 2));
    this.doc.text(lines, this.margin, this.currentY);
    
    this.currentY += lines.length * 5 + 10;
  }

  private addProfessionalFooter(report: Report): void {
    const footerY = this.pageHeight - 15;
    
    // Footer line
    this.doc.setDrawColor(this.mediumGray.r, this.mediumGray.g, this.mediumGray.b);
    this.doc.setLineWidth(0.5);
    this.doc.line(this.margin, footerY - 10, this.pageWidth - this.margin, footerY - 10);
    
    // Chain of custody signature
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(this.mediumGray.r, this.mediumGray.g, this.mediumGray.b);
    this.doc.text('Chain-of-Custody Signature', this.margin, footerY);
    
    // Company info
    this.doc.text('Generated by Skimbuster', this.pageWidth - 80, footerY);
    
    // Organization
    const orgName = report.user?.masterOwnerID?.substring(0, 15) || 'Organization';
    this.doc.text(`— ${orgName}`, this.pageWidth - 30, footerY);
  }

  private addNewPage(): void {
    this.doc.addPage();
    this.currentY = this.margin;
  }

  private getReportCoordinates(report: Report): { lat: number | null, lng: number | null } {
    const anyReport = report as any;
    
    if (anyReport.location?.latitude && anyReport.location?.longitude) {
      return { lat: anyReport.location.latitude, lng: anyReport.location.longitude };
    }
    if (anyReport.location?.coordinates?.latitude && anyReport.location?.coordinates?.longitude) {
      return { lat: anyReport.location.coordinates.latitude, lng: anyReport.location.coordinates.longitude };
    }
    if (anyReport.readerDetails?.device?.coordinates?.latitude && anyReport.readerDetails?.device?.coordinates?.longitude) {
      return { lat: anyReport.readerDetails.device.coordinates.latitude, lng: anyReport.readerDetails.device.coordinates.longitude };
    }
    if (anyReport.readerDetails?.coordinates?.latitude && anyReport.readerDetails?.coordinates?.longitude) {
      return { lat: anyReport.readerDetails.coordinates.latitude, lng: anyReport.readerDetails.coordinates.longitude };
    }
    if (anyReport.coordinates?.latitude && anyReport.coordinates?.longitude) {
      return { lat: anyReport.coordinates.latitude, lng: anyReport.coordinates.longitude };
    }
    
    return { lat: null, lng: null };
  }

  private extractScanData(report: any): any {
    // Extract or generate scan data from the report
    // This would ideally come from actual scan data in the report
    
    const isSkimmerDetected = report.status === 'Skimmer Detected!';
    
    return {
      detectionScore: isSkimmerDetected ? '9.2 / 10' : '2.1 / 10',
      scanDuration: '650 ms',
      rawShiftPeak: isSkimmerDetected ? '+113 M' : '+12 M',
      extremeSpikes: isSkimmerDetected ? '2' : '0',
      magneticSignature: isSkimmerDetected ? 'LDC1612 Magnetic Signature' : 'Normal Baseline'
    };
  }

  private sanitizeTextValue(value: any, defaultValue: string = ''): string {
    if (value === null || value === undefined) {
      return defaultValue;
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return defaultValue;
      }
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (typeof value === 'number') {
      return value.toString();
    }
    const stringValue = String(value).trim();
    return stringValue || defaultValue;
  }

  save(filename: string): void {
    this.doc.save(filename);
  }

  output(type: 'arraybuffer' | 'blob' | 'bloburi' | 'dataurlstring' = 'blob'): any {
    const jsPDFType = type === 'dataurlstring' ? 'datauri' as const : type;
    return this.doc.output(jsPDFType as any);
  }
}

/** Fetch company profile from Firestore for PDF header (by companyId or masterOwnerID) */
async function fetchCompanyForReport(report: ExtendedReport): Promise<CompanyProfile | null> {
  const companyId = (report as any).user?.companyId || (report as any).user?.masterOwnerID;
  if (!companyId) return null;
  try {
    const snap = await getDoc(doc(db, 'companies', String(companyId)));
    if (snap.exists()) return { id: snap.id, ...snap.data() } as CompanyProfile;
  } catch (e) {
    console.warn('Could not fetch company for PDF:', e);
  }
  return null;
}

/** Generate PDF from mobile app HTML template (same layout as app) using html2canvas. Runs in browser only. */
async function generatePdfFromMobileHtml(
  report: ExtendedReport,
  company: MobileCompanyProfile | null,
  filename: string
): Promise<void> {
  if (typeof document === 'undefined') {
    throw new Error('PDF from HTML requires browser environment');
  }
  const html = buildMobileReportHtml(report as any, company);
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = '816px';
  container.style.background = 'white';
  container.style.zIndex = '-1';
  container.innerHTML = html;
  document.body.appendChild(container);

  const waitForImages = (el: HTMLElement): Promise<void> => {
    const imgs = el.querySelectorAll('img');
    if (!imgs.length) return Promise.resolve();
    return Promise.all(
      Array.from(imgs).map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) resolve();
            else img.onload = () => resolve();
            img.onerror = () => resolve();
            setTimeout(resolve, 3000);
          })
      )
    ).then(() => {});
  };

  try {
    await waitForImages(container);
    const canvas = await html2canvas(container, {
      scale: 1,
      useCORS: true,
      allowTaint: true,
      width: 816,
      windowWidth: 816,
      backgroundColor: '#ffffff',
    });
    document.body.removeChild(container);

    const imgW = 816;
    const imgH = canvas.height;
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: [595.28, 841.89] });
    const pageW = pdf.internal.pageSize.width;
    const pageH = pdf.internal.pageSize.height;
    const scaledW = pageW;
    const scaledH = (imgH * pageW) / imgW;
    const imgData = canvas.toDataURL('image/jpeg', 0.92);

    let heightLeft = scaledH;
    let position = 0;
    pdf.addImage(imgData, 'JPEG', 0, position, scaledW, scaledH);
    heightLeft -= pageH;
    while (heightLeft > 0) {
      pdf.addPage();
      position = position - pageH;
      pdf.addImage(imgData, 'JPEG', 0, position, scaledW, scaledH);
      heightLeft -= pageH;
    }
    pdf.save(filename);
  } catch (e) {
    document.body.removeChild(container);
    throw e;
  }
}

export const generateEnhancedPDF = async (report: ExtendedReport, options: ReportGenerationOptions = {}) => {
  const viewerId = options.viewerMasterOwnerId?.trim();
  if (!viewerId) {
    throw new Error('You must be signed in to download reports.');
  }
  if (!isSameOrganizationReport(report, viewerId)) {
    throw new Error('You can only download reports from your own company.');
  }

  const company = await fetchCompanyForReport(report);
  const filename = `skimbuster_report_${(report.id || 'report').substring(0, 8)}_${new Date().toISOString().split('T')[0]}.pdf`;

  const useHtmlMobile = options.mobileStyle !== false && typeof document !== 'undefined';
  if (useHtmlMobile) {
    await generatePdfFromMobileHtml(report, company as MobileCompanyProfile | null, filename);
    return;
  }

  const generator = new EnhancedPDFGenerator();
  await generator.generateIncidentReport(report, { mobileStyle: true, ...options }, company);
  generator.save(filename);
};
